import { type LoaderFunctionArgs, type MetaFunction, json, redirect } from '@remix-run/node';
import { Link, useLoaderData } from '@remix-run/react';
import { useRef } from 'react';
import { z } from 'zod';
import { Button } from '~/components/ui/button.js';
import { Icon } from '~/components/ui/icon.js';
import {
  ListingImagesSection,
  ListingMainInfoSection,
  ListingSectionLinks,
} from '~/routes/app+/_dashboard+/_guest+/listing.$id.js';
import { buildMeta } from '~/utils/meta.js';
import { useIsAuthenticated } from '~/utils/user.js';
import { zParseOrRedirect } from '~/utils/zod.server.js';

const paramsSchema = z.object({
  id: z.string(),
});

export async function loader({ params, context }: LoaderFunctionArgs) {
  const { id } = zParseOrRedirect(params, paramsSchema, '/404');
  const session = await context.authSession.getOptionalSession();

  if (session) {
    return redirect(`/app/listing/${id}`);
  }

  const listing = await context.db.listing.findUnique({
    where: { id },
    select: {
      id: true,
      images: {
        select: {
          file: {
            select: {
              url: true,
            },
          },
        },
      },
      totalBedrooms: true,
      totalBathrooms: true,
      type: true,
      bedrooms: {
        select: {
          id: true,
          type: true,
          availableFrom: true,
          availableTo: true,
          price: true,
        },
      },
      user: {
        select: {
          id: true,
          firstName: true,
          lastName: true,
          university: true,
          graduationYear: true,
          avatar: {
            select: { url: true },
          },
        },
      },
    },
  });

  if (!listing) {
    throw redirect('/404');
  }

  return json({
    listing: {
      ...listing,
      roommates: [],
    },
  });
}

export const meta: MetaFunction<typeof loader> = (args) => {
  const { data } = args;
  if (!data) {
    return buildMeta(args);
  }

  const listing = data.listing;

  const typeLabel = listing.type === 'HOUSE' ? 'house' : 'apartment';

  const imageLink = listing.images[0].file.url;

  const title = 'hostU | Find your place';
  const description = `${listing.totalBedrooms} bedroom(s), ${listing.totalBathrooms} bathroom(s) ${typeLabel}`;

  return buildMeta(args, {
    title,
    description,
    image: imageLink,
  });
};

const listingSections = [
  { id: 'overview', label: 'Overview', default: true },
  { id: 'about', label: 'About the place' },
  { id: 'amenities', label: 'Amenities' },
];

const amenities = [
  {
    type: 'air-conditioning',
    label: 'Air Conditioning',
  },
  { type: 'backyard', label: 'Backyard' },
  { type: 'ceiling-fan', label: 'Ceiling Fan' },
  { type: 'closet-space', label: 'Closet Space' },
  { type: 'furnished', label: 'Furnished' },
  { type: 'dishwasher', label: 'Dishwasher' },
  { type: 'dryer', label: 'Dryer' },
  {
    type: 'entry-security',
    label: 'Entry Security',
  },
  { type: 'freezer', label: 'Freezer' },
  { type: 'refrigerator', label: 'Refrigerator' },
  { type: 'heater', label: 'Heater' },
  { type: 'oven', label: 'Oven' },
  { type: 'patio', label: 'Patio' },
  {
    type: 'security-system',
    label: 'Security System',
  },
  { type: 'stove', label: 'Stove' },
  { type: 'pool-access', label: 'Swimming pool' },
  { type: 'washer', label: 'Washer' },
  { type: 'tv', label: 'TV' },
  { type: 'wifi', label: 'WiFi' },
];

export default function SharedListing() {
  const { listing } = useLoaderData<typeof loader>();
  const verifyContainerRef = useRef<HTMLDivElement>(null);
  const isAuthenticated = useIsAuthenticated();

  const title = listing.user.firstName.endsWith('s')
    ? `${listing.user.firstName}' room`
    : `${listing.user.firstName}'s room`;

  return (
    <article className="flex flex-col items-center justify-start pt-4">
      <div className="h-full w-full max-w-[1200px]">
        <div className="flex w-full flex-row items-center justify-between gap-2 px-4">
          <div>
            <h1 className="font-semibold text-xl xs:text-2xl sm:text-4xl md:text-3xl">{title}</h1>
            <h2 className="text-gray-500 text-sm xs:text-base sm:text-lg md:text-xl">
              Private room in a {listing.totalBedrooms}
              {' bedroom '}
              {listing.type === 'HOUSE' ? 'house' : 'apartment'}
            </h2>
          </div>
        </div>
        <div className="mt-6 flex flex-col gap-8 px-4">
          <ListingImagesSection
            images={listing.images}
            onShowImages={() => {
              if (!verifyContainerRef.current) {
                return;
              }

              verifyContainerRef.current.scrollIntoView({ behavior: 'smooth' });
            }}
          />
          <ListingSectionLinks sections={listingSections} />
          <div className="flex flex-col-reverse justify-between gap-4 lg:flex-row">
            <div className="flex w-full flex-col gap-8">
              <ListingMainInfoSection
                user={listing.user}
                totalBedrooms={listing.totalBedrooms}
                totalBathrooms={listing.totalBathrooms}
                type={listing.type}
                roommatesCount={listing.roommates.length}
                entirePlace={{
                  price: listing.bedrooms[0].price,
                  availableFrom: listing.bedrooms[0].availableFrom,
                  availableTo: listing.bedrooms[0].availableTo,
                }}
              />
            </div>
          </div>
        </div>

        <div className="h-12" />
        <div
          ref={verifyContainerRef}
          className="flex w-full items-center justify-center bg-hostU-blue-gradient px-2 py-24"
        >
          <div className="w-full rounded-lg bg-white px-4 py-6 sm:w-auto sm:px-20 sm:pt-20 sm:pb-14">
            <div className="flex items-start gap-3 sm:gap-5">
              <div className="aspect-square rounded-xl bg-[#F6F3F3] p-3 sm:p-5">
                <Icon name="blue-checkmark" className="h-14 w-14 sm:h-14 sm:w-14" />
              </div>
              <div>
                <p className="font-bold text-lg sm:text-xl">Get verified</p>
                <p className="sm:text-lg">to see the full listing and others</p>
                <p className="text-slate-500">(it takes 3 minutes).</p>
              </div>
            </div>
            <Button className="mx-auto mt-11 flex h-9 w-44 bg-[#4FBEEE]" asChild>
              {!isAuthenticated ? (
                <Link to={`/signup?redirectTo=/app/listing/${listing.id}`}>Verify now</Link>
              ) : (
                <Link to={`/app/listing/${listing.id}`}>Go to listing</Link>
              )}
            </Button>
          </div>
        </div>
      </div>
    </article>
  );
}
